import moment from 'moment/moment'

import { currencyFloat } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'

export class RecordDataMap {
    constructor(data?: any) {
        if (!!data) Object.assign(this, data)
    }

    serialize(): Record<string, any> {
        return {
            ...this,
        }
    }

    deserialize(obj: any): void {
        Object.assign(this, obj)
    }

    hasValueForKey(key: string): boolean {
        return key in this
    }

    containsKey(key: string): boolean {
        return key in this
    }

    [key: string]: any

    get(key: string) {
        if (key in this) return this[key]
        return undefined
    }

    put(key: string, value: any) {
        this[key] = value
    }

    valueForBoolKey(key: string): boolean {
        const val = this.get(key)
        if (!val) return false

        return (
            val === true ||
            val?.toLowerCase() === 'true' ||
            (typeof val === 'string' && !!val.length && !isNaN(Number(val)) && Number(val) === 1)
        )
    }

    valueForKey(key: string): string | undefined {
        const val = this.get(key)
        if (!val) return undefined
        return val
    }

    valueForDateKey(key: string): moment.Moment | undefined {
        if (!this.hasValueForKey(key)) return undefined
        const value = this.get(key)
        if (moment.isMoment(value)) return value
        if (typeof value === 'number') {
            return moment.unix(value)
        }
        if (typeof value === 'string') {
            let date = moment(value, 'YYYY-MM-DD', true)
            if (!date.isValid()) {
                date = moment(value, 'YYYY-MM-DDTHH:mm:ss', true)
            }
            return date.isValid() ? date : undefined
        }
        return undefined
    }

    valueForTimeStampKey(key: string): moment.Moment | undefined {
        const val = this.get(key)
        if (!val) return undefined
        return moment(val)
    }

    valueForTimeStampListKey(key: string): moment.Moment[] {
        const list = this.valueForListKey(key)
        if (list) {
            return list.map((value: any) => {
                if (typeof value === 'string') return moment(value)
                return value
            })
        }
        return []
    }

    valueForListKey(key: string): any[] {
        const val = this.get(key) as any
        if (!val) return []
        if (!Array.isArray(val)) return [val]
        return val as any[]
    }

    valueForRecordListKey<T extends BaseRecord>(key: string, model: any): T[] {
        const val = this.get(key) as T[]
        if (!val) {
            return []
        }
        if (!Array.isArray(val)) {
            const item = this.valueForRecordKey<T>(key, model)
            if (!!item) return [item]
            return []
        }

        return val
            .map((item) => {
                if (item instanceof BaseRecord) {
                    return item as T
                }
                if (typeof item === 'object' && item !== null) {
                    return new model(item) as T
                }
                if (typeof item === 'string') {
                    return CacheFactory.getInstance().getRecord(model, item) as T
                }
                return undefined
            })
            .filter((item) => !!item) as T[]
    }

    valueForDictionaryKey(key: string): RecordDataMap | undefined {
        const val = this.get(key)
        if (!val) return undefined
        if (val instanceof RecordDataMap) return val
        if (val instanceof Map || typeof val === 'object') return new RecordDataMap(val)
        if (typeof val === 'string') {
            try {
                return new RecordDataMap(JSON.parse(val))
            }
            catch (e) {
                return undefined
            }
        }
        return undefined
    }

    valueForNumberKey(key: string): number | undefined {
        const val = this.get(key)
        if (val !== 0 && !val) return undefined
        return Number(val)
    }

    valueForCurrencyKey(key: string): number | undefined {
        return currencyFloat(this.valueForNumberKey(key))
    }

    valueForIntKey(key: string): number | undefined {
        const val = this.get(key)
        if (val !== 0 && !val) return undefined
        return parseInt(Number(val).toString())
    }

    valueForRecordKey<T extends BaseRecord>(key: string, model: any): T | undefined {
        const val = this.get(key)
        if (!val) return undefined
        if (val instanceof BaseRecord) return val as T
        if (typeof val === 'string') return CacheFactory.getInstance().getRecord(model, val) as T
    }

    valueForImageKey<T extends BaseRecord>(key: string, model: any): T | undefined {
        const val = this.valueForRecordKey<T>(key, model)
        if (!!val) return val
        if (typeof val == 'string' && !!val && this.hasValueForKey(`${key}_uri`)) return undefined
        return new model(undefined, {
            id: this.valueForKey(key),
            uri: this.valueForKey(`${key}_uri`),
        })
    }
}
